// ─── MobileNavBar ─────────────────────────────────────────────────────────────
// Mobile-only site header (< 768px), matching the XD mobile frame: one white
// row holding the hamburger (left), the WQData LIVE logo (centre), and a
// right-hand icon cluster (Help Center + account).
//
// It REPLACES the stacked mobile chrome — the blue Sign In / Messages / Help
// Center / nexsens.com bar plus the separate logo row — which wrapped onto
// three cramped lines on a 390px screen. Tablet and desktop (>= 768px) keep
// the existing two-row header untouched; this component renders under
// `md:hidden` and the blue bar under `hidden md:block`.
//
// TWO INDEPENDENT PANELS. Each icon owns exactly one, and neither can open
// the other:
//
//   Hamburger (left)  → MAIN NAVIGATION
//        signed in : Dashboard · Data Analytics · Report · Gallery ·
//                    Settings · Live (+ their sub-items), plus Messages.
//                    Driven by useHeaderNav — the same tab list, localStorage
//                    keys and deep-link rules the desktop header uses, not a
//                    second copy of them.
//        signed out : About / Features / Demo / Plans / Get Started, the
//                    marketing links that are `hidden md:flex` on desktop.
//
//   Account (right)   → ACCOUNT MENU ONLY
//        signed in : the real <UserDropdown variant="mobile" /> — Account,
//                    Two-Factor Authentication, project list, Add Project,
//                    SUPER ADMIN, Log Out.
//        signed out : opens the existing SignInModal.
//
// Help Center keeps its own icon, and nexsens.com sits at the foot of the
// navigation panel — both were in the blue bar that mobile no longer shows,
// so they have to live somewhere or the redesign would drop them.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useHeaderNav } from '../lib/useHeaderNav'

const HelpIcon = ({ className = '' }) => (
  <svg className={className} width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 17V16.5M12 13.5C12 13.5 14 12 14 10C14 8.895 13.105 8 12 8C10.895 8 10 8.895 10 10"
          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

const AccountIcon = ({ className = '' }) => (
  <svg className={className} width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="1.5" />
    <path d="M6.5 18.5C7.5 16.5 9.5 15.5 12 15.5C14.5 15.5 16.5 16.5 17.5 18.5"
          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

const Chevron = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"
       className={`transition-transform ${open ? 'rotate-180' : ''}`}>
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

// Marketing anchors — same targets and order as the desktop nav in
// Navigation.jsx, which hides them below `md`.
const MARKETING_LINKS = [
  { label: 'About WQData LIVE', href: '#about' },
  { label: 'Features',         href: '#features' },
  { label: 'Demo',             href: '#projects' },
  { label: 'Plans',            href: '#plans' },
]

const ROW = 'w-full text-left px-5 py-3.5 text-[15px] font-bold text-[#4D4D4D] border-b border-gray-100 hover:bg-gray-50 transition flex items-center justify-between gap-3'

/** Slide-over panel shared by both menus, so they look and behave identically. */
function Drawer({ open, onClose, side = 'left', label, children }) {
  // Body scroll lock while open, restored on close so a mid-drawer route
  // change can't leave the page permanently unscrollable.
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])

  // Escape closes, matching the modals used elsewhere in the app.
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-[100] md:hidden">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={`absolute inset-y-0 ${side === 'left' ? 'left-0' : 'right-0'}
                    w-[86%] max-w-[340px] bg-white shadow-xl flex flex-col`}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
          <img src="/images/logo.png" alt="WQData LIVE" className="h-7 w-auto" />
          <button onClick={onClose} aria-label="Close menu" className="p-1 text-[#4D4D4D]">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto z-[9]">{children}</div>
      </div>
    </div>
  )
}

export function MobileNavBar({
  isAuthenticated = false,
  unread = 0,
  onSignInClick = () => {},
  // The real <UserDropdown variant="mobile" />, passed in so this component
  // shows the actual account menu rather than a second copy of its items.
  accountMenu = null,
}) {
  // Two separate pieces of state — the whole point of this component. A
  // single shared `drawerOpen` is what previously made the hamburger open
  // the account menu.
  const [navOpen, setNavOpen]         = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  // Which nav tab is expanded to show its sub-items.
  const [expanded, setExpanded]       = useState(null)

  // Same navigation the desktop header uses: tab list, sub-items, and the
  // localStorage deep-link contract with DashboardPage.
  const { tabs, hasDropdown, dropdownConfig, goToDashboard } = useHeaderNav({ enabled: isAuthenticated })

  // Opening one always closes the other, so the two can never be on screen
  // together and neither can be mistaken for the other.
  const openNav     = () => { setAccountOpen(false); setNavOpen(true) }
  const openAccount = () => { setNavOpen(false); setAccountOpen(true) }

  const navigateTo = (args) => { setNavOpen(false); setExpanded(null); goToDashboard(args) }

  return (
    <div className="md:hidden">
      {/* Single white header row */}
      <div className="bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-2.5">
          {/* Hamburger → MAIN NAVIGATION */}
          <button
            onClick={openNav}
            aria-label="Open navigation menu"
            aria-expanded={navOpen}
            className="p-1 -ml-1 text-[#358ECD]"
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>

          {/* Centred logo */}
          <Link to="/" className="flex-1 flex justify-center px-2" aria-label="WQData LIVE home">
            <img src="/images/logo.png" alt="WQData LIVE" className="h-12 w-auto" />
          </Link>

          {/* Right cluster: account + Help Center. On mobile the account
              (profile) icon comes FIRST, then Help — matches the mobile XD
              (desktop header keeps its own order). */}
          <div className="flex items-center gap-1 text-[#358ECD]">
            {isAuthenticated ? (
              // Account icon → ACCOUNT MENU ONLY. It opens as a panel rather
              // than a dropdown because a menu anchored to a 26px icon on a
              // 390px screen would overflow the viewport.
              <button
                onClick={openAccount}
                aria-label="Account menu"
                aria-expanded={accountOpen}
                className="p-1 relative"
              >
                <AccountIcon />
                {unread > 0 && (
                  <span className="absolute top-0 right-0 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-4">
                    {unread > 99 ? '99+' : unread}
                  </span>
                )}
              </button>
            ) : (
              <button onClick={onSignInClick} aria-label="Sign in" className="p-1">
                <AccountIcon />
              </button>
            )}
            <a href="/help" aria-label="Help Center" className="p-1">
              <HelpIcon />
            </a>
          </div>
        </div>
      </div>

      {/* ── Hamburger panel: MAIN NAVIGATION ───────────────────────────── */}
      <Drawer open={navOpen} onClose={() => setNavOpen(false)} side="left" label="Navigation menu">
        {isAuthenticated ? (
          <>
            {tabs.map((tab) => {
              const sub = hasDropdown(tab) ? (dropdownConfig[tab]?.items || []) : []
              const isOpen = expanded === tab
              // Gallery has no sub-items, so it navigates on tap. The rest
              // expand in place — same relationship as the desktop dropdowns.
              if (!sub.length) {
                return (
                  <button key={tab} onClick={() => navigateTo({ tab })} className={ROW}>
                    {tab}
                  </button>
                )
              }
              return (
                <div key={tab}>
                  <button
                    onClick={() => setExpanded(isOpen ? null : tab)}
                    aria-expanded={isOpen}
                    className={ROW}
                  >
                    <span>{tab}</span>
                    <Chevron open={isOpen} />
                  </button>
                  {isOpen && (
                    <div className="bg-gray-50 border-b border-gray-100">
                      {sub.map((item, idx) => {
                        // Most tabs carry plain-string items; the Report tab
                        // carries { label, report } objects (dynamic saved
                        // reports). Render the label either way.
                        const itemLabel = typeof item === 'string' ? item : item.label
                        return (
                          <button
                            key={`${tab}-${idx}-${itemLabel}`}
                            onClick={() => navigateTo({
                              tab, indexKey: dropdownConfig[tab].indexKey, indexValue: idx,
                            })}
                            className="w-full text-left pl-9 pr-5 py-3 text-sm text-[#4D4D4D] hover:bg-gray-100 transition"
                          >
                            {itemLabel}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}

            <a href="/messages" onClick={() => setNavOpen(false)} className={ROW}>
              <span>Messages</span>
              {unread > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold leading-none">
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </a>
          </>
        ) : (
          <>
            {MARKETING_LINKS.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setNavOpen(false)} className={ROW}>
                {l.label}
              </a>
            ))}
            <a href="/login" onClick={() => setNavOpen(false)} className={`${ROW} text-[#358ECD]`}>
              Get Started
            </a>
          </>
        )}

        <a
          href="https://www.nexsens.com"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setNavOpen(false)}
          className={ROW}
        >
          nexsens.com
        </a>
      </Drawer>

      {/* ── Account panel: ACCOUNT MENU ONLY ───────────────────────────── */}
      <Drawer open={accountOpen} onClose={() => setAccountOpen(false)} side="right" label="Account menu">
        {accountMenu}
      </Drawer>
    </div>
  )
}
